import React, { useState, useEffect } from 'react';
import { 
  ChevronDown, 
  Check, 
  Shield,
  Users,
  Package,
  FileText,
  CreditCard,
  Zap,
  Calendar,
  Rocket,
  HelpCircle,
  Play,
  Store,
  PenTool,
  ExternalLink,
  Sparkles,
  Link2
} from 'lucide-react';
import { orgApi } from '../utils/apiV2';
import { BACKEND_URL } from '../config/api';

const SetupGuideWidget = ({ onNavigate, isVisible, onToggleVisibility, onOpenSettings }) => {
  const [isMinimized, setIsMinimized] = useState(true);
  const [hasCheckedCompletion, setHasCheckedCompletion] = useState(false);
  const [completionData, setCompletionData] = useState({
    hasRole: false,
    hasTeamMember: false,
    hasAsset: false,
    hasWaiverText: false,
    hasStripeConnect: false,
    hasService: false,
    hasBooking: false,
    hasFlow: false,
    hasBlog: false,
    stripeIsLive: false
  });
  const [loading, setLoading] = useState(true);

  // Fetch completion status - always fetch even when minimized
  useEffect(() => {
    const fetchCompletionStatus = async () => {
      try {
        // Get org first to get orgId for flows
        const orgRes = await orgApi.getOrganization().catch(() => ({ data: {} }));
        const org = orgRes.data || {};
        const orgId = org.id;
        
        // Fetch all data in parallel
        const [rolesRes, teamRes, assetsRes, servicesRes, bookingsRes, flowsRes, blogsRes] = await Promise.all([
          orgApi.getRoles().catch(() => ({ data: { roles: [] } })),
          orgApi.getTeamMembers().catch(() => ({ data: { teamMembers: [] } })),
          orgApi.getAssets().catch(() => ({ data: { assets: [] } })),
          orgApi.getServices().catch(() => ({ data: { services: [] } })),
          orgApi.getBookings().catch(() => ({ bookings: [] })),
          // Fetch flows directly
          orgId ? fetch(`${BACKEND_URL}/api/flows/org/${orgId}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          }).then(r => r.json()).catch(() => ({ flows: [] })) : Promise.resolve({ flows: [] }),
          // Fetch blog posts
          orgId ? fetch(`${BACKEND_URL}/api/v2/blogs/organizations/${orgId}/blogs`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          }).then(r => r.json()).catch(() => ({ blogs: [] })) : Promise.resolve({ blogs: [] })
        ]);

        const roles = rolesRes.data?.roles || rolesRes.data || [];
        const teamData = teamRes.data?.teamMembers || teamRes.data || [];
        const assets = assetsRes.data?.assets || assetsRes.data || [];
        const services = servicesRes.data?.services || servicesRes.data || [];
        const bookings = bookingsRes?.bookings || bookingsRes || [];
        const flows = flowsRes?.flows || [];
        const blogs = blogsRes?.blogs || blogsRes || [];
        
        const waiverText = org.customWaiverText || org.waiverText || '';
        const hasWaiverText = waiverText.trim().length >= 50 || org.waiversEnabled === true;
        
        // Stripe is "live" when in live mode (not test mode)
        const stripeIsLive = false; // Will be updated when we add live mode detection
        
        setCompletionData({
          hasRole: roles.length > 0,
          hasTeamMember: teamData.length > 0,
          hasAsset: assets.length > 0,
          hasWaiverText: hasWaiverText,
          hasStripeConnect: !!(org.stripeConnectAccountId && org.stripeConnectChargesEnabled),
          hasService: services.length > 0,
          hasBooking: bookings.length > 0,
          hasFlow: flows.length > 0,
          hasBlog: Array.isArray(blogs) && blogs.length > 0,
          stripeIsLive: stripeIsLive,
          orgSlug: org.slug,
          orgId: org.id
        });
      } catch (error) {
        console.error('Error fetching setup status:', error);
      } finally {
        setLoading(false);
      }
    };

    // Always fetch data on mount
    fetchCompletionStatus();
    
    // Refresh periodically
    const interval = setInterval(fetchCompletionStatus, 30000);
    
    const handleDataChange = () => fetchCompletionStatus();
    window.addEventListener('setupDataChanged', handleDataChange);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('setupDataChanged', handleDataChange);
    };
  }, []);

  // Auto-expand when there are incomplete steps
  useEffect(() => {
    if (loading || hasCheckedCompletion) return;
    
    const coreComplete = completionData.hasAsset && 
      completionData.hasRole && 
      completionData.hasTeamMember && 
      completionData.hasService &&
      completionData.hasWaiverText &&
      completionData.hasStripeConnect &&
      completionData.hasFlow;
    
    if (!coreComplete) {
      setIsMinimized(false);
    }
    setHasCheckedCompletion(true);
  }, [completionData, loading, hasCheckedCompletion]);

  // PHASE 1: Core Setup (Build your business)
  // PHASE 2: Set Up Payments
  // PHASE 3: Create Booking Flow (how customers book)
  // PHASE 4: Test Everything
  // PHASE 5: Explore Features
  // PHASE 6: Go Live!
  
  const phases = [
    {
      name: 'Build Your Business',
      description: 'Set up the core of your operation',
      steps: [
        {
          id: 'asset',
          title: 'Register an Asset',
          description: 'Add boats, venues, vehicles, etc.',
          icon: Package,
          color: 'bg-green-500',
          isComplete: completionData.hasAsset,
          navigateTo: 'assets'
        },
        {
          id: 'role',
          title: 'Create a Role',
          description: 'Define positions (Captain, Guide)',
          icon: Shield,
          color: 'bg-blue-500',
          isComplete: completionData.hasRole,
          navigateTo: 'roles'
        },
        {
          id: 'team',
          title: 'Add Team Member',
          description: 'Add yourself or staff',
          icon: Users,
          color: 'bg-indigo-500',
          isComplete: completionData.hasTeamMember,
          navigateTo: 'teamMembers'
        },
        {
          id: 'service',
          title: 'Create a Service',
          description: 'Build bookable experiences',
          icon: Zap,
          color: 'bg-pink-500',
          isComplete: completionData.hasService,
          navigateTo: 'services'
        }
      ]
    },
    {
      name: 'Set Up Payments',
      description: 'Configure payments (test mode first)',
      steps: [
        {
          id: 'waiver',
          title: 'Set Up Waiver',
          description: 'Liability protection for bookings',
          icon: FileText,
          color: 'bg-amber-500',
          isComplete: completionData.hasWaiverText,
          navigateTo: 'waivers'
        },
        {
          id: 'stripe',
          title: 'Connect Stripe (Test Mode)',
          description: 'Set up payments - starts in test mode',
          icon: CreditCard,
          color: 'bg-purple-500',
          isComplete: completionData.hasStripeConnect,
          navigateTo: 'payments',
          hint: completionData.hasStripeConnect ? '✓ Connected in test mode' : null
        }
      ]
    },
    {
      name: 'Create Booking Flow',
      description: 'This is how customers book you',
      steps: [
        {
          id: 'flow',
          title: 'Create a Booking Flow',
          description: 'Embed on your website or share link',
          icon: Link2,
          color: 'bg-violet-500',
          isComplete: completionData.hasFlow,
          navigateTo: 'flows',
          hint: 'Flows are your booking widgets'
        }
      ]
    },
    {
      name: 'Test Everything',
      description: 'Make sure it all works before going live',
      steps: [
        {
          id: 'test-booking',
          title: 'Make a Test Booking',
          description: 'Use test card: 4242 4242 4242 4242',
          icon: Play,
          color: 'bg-orange-500',
          isComplete: completionData.hasBooking,
          navigateTo: 'flows', // Navigate to flows to test booking
          isGated: !completionData.hasStripeConnect,
          gateMessage: 'Connect Stripe first',
          hint: 'Test the full customer experience'
        },
        {
          id: 'check-dashboard',
          title: 'Check Your Dashboard',
          description: 'See booking on calendar & relations',
          icon: Calendar,
          color: 'bg-teal-500',
          isComplete: completionData.hasBooking,
          navigateTo: 'bookings', // Navigate to bookings calendar
          isGated: !completionData.hasBooking,
          gateMessage: 'Make a test booking first'
        }
      ]
    },
    {
      name: 'Explore Features',
      description: 'Optional but powerful',
      isOptional: true,
      steps: [
        {
          id: 'marketplace',
          title: 'View Your Marketplace Page',
          description: 'See how customers find you',
          icon: Store,
          color: 'bg-cyan-500',
          isComplete: completionData.hasService,
          isExternal: true,
          externalUrl: completionData.orgId ? `/host/${completionData.orgId}` : '/search',
          hint: 'Your public profile'
        },
        {
          id: 'seo-blog',
          title: 'Generate SEO Content',
          description: 'AI-powered content for visibility',
          icon: PenTool,
          color: 'bg-rose-500',
          isComplete: completionData.hasBlog,
          navigateTo: 'blog',
          hint: 'Boost your search rankings'
        }
      ]
    },
    {
      name: 'Go Live! 🚀',
      description: 'Start accepting real payments',
      isFinal: true,
      steps: [
        {
          id: 'go-live',
          title: 'Switch Stripe to Live Mode',
          description: 'Enable real payments',
          icon: Rocket,
          color: 'bg-emerald-500',
          isComplete: completionData.stripeIsLive,
          navigateTo: 'payments',
          isGated: !completionData.hasBooking,
          gateMessage: 'Complete test booking first',
          hint: completionData.stripeIsLive ? "You're live! 🎉" : 'Final step to launch'
        }
      ]
    }
  ];

  // Count completed steps
  const allSteps = phases.flatMap(p => p.steps);
  const coreSteps = phases.filter(p => !p.isOptional && !p.isFinal).flatMap(p => p.steps);
  const completedCoreSteps = coreSteps.filter(s => s.isComplete).length;
  const totalCoreSteps = coreSteps.length;
  const progressPercent = Math.round((completedCoreSteps / totalCoreSteps) * 100);

  const handleStepClick = (step) => {
    if (step.isGated) return;
    
    if (step.isExternal && step.externalUrl) {
      window.open(step.externalUrl, '_blank');
      return;
    }
    
    if (onNavigate && step.navigateTo) {
      onNavigate(step.navigateTo);
    }
  };

  // Minimized state
  if (isMinimized || !isVisible) {
    const handleOpenGuide = () => {
      setIsMinimized(false);
      if (!isVisible) {
        onToggleVisibility(true);
      }
    };
    
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={handleOpenGuide}
          className="relative p-3 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 transition-all shadow-sm hover:shadow-md cursor-pointer"
          title="Open Setup Guide"
          data-testid="setup-guide-toggle"
        >
          <HelpCircle className="w-6 h-6" />
          {completedCoreSteps < totalCoreSteps && (
            <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1.5 bg-blue-600 rounded-full text-[10px] font-bold flex items-center justify-center text-white">
              {totalCoreSteps - completedCoreSteps}
            </span>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5" />
            <h3 className="font-semibold text-lg">Setup Guide</h3>
          </div>
          <button
            onClick={() => setIsMinimized(true)}
            className="p-1 hover:bg-white/20 rounded transition-colors"
            title="Minimize"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>
        
        {/* Progress Bar */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-2 bg-white/30 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <span className="text-sm font-medium">{completedCoreSteps}/{totalCoreSteps}</span>
        </div>
        
        {/* Test Mode Badge */}
        {completionData.hasStripeConnect && !completionData.stripeIsLive && (
          <div className="mt-2 px-2 py-1 bg-amber-400/20 rounded-lg flex items-center gap-2">
            <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
            <span className="text-xs font-medium">Preview Mode - Test payments enabled</span>
          </div>
        )}
      </div>

      {/* Phases & Steps */}
      <div className="max-h-[400px] overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center text-gray-500">
            <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
            Loading...
          </div>
        ) : (
          <div className="p-2">
            {phases.map((phase, phaseIdx) => {
              const phaseComplete = phase.steps.every(s => s.isComplete);
              const isOptional = phase.isOptional;
              const isFinal = phase.isFinal;
              
              return (
                <div key={phase.name} className="mb-3">
                  {/* Phase Header */}
                  <div className={`px-3 py-2 rounded-lg mb-1 ${
                    phaseComplete ? 'bg-green-50' : 
                    isFinal ? 'bg-gradient-to-r from-emerald-50 to-teal-50' :
                    isOptional ? 'bg-gray-50' : 'bg-blue-50'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className={`text-xs font-semibold uppercase tracking-wide ${
                          phaseComplete ? 'text-green-600' :
                          isFinal ? 'text-emerald-600' :
                          isOptional ? 'text-gray-500' : 'text-blue-600'
                        }`}>
                          {isOptional && '✨ '}{phase.name}
                        </span>
                        <p className="text-xs text-gray-500 mt-0.5">{phase.description}</p>
                      </div>
                      {phaseComplete && <Check className="w-4 h-4 text-green-500" />}
                    </div>
                  </div>
                  
                  {/* Steps */}
                  {phase.steps.map((step) => {
                    const Icon = step.icon;
                    const isComplete = step.isComplete;
                    const isGated = step.isGated;
                    
                    return (
                      <button
                        key={step.id}
                        onClick={() => handleStepClick(step)}
                        disabled={isGated}
                        className={`w-full text-left p-2.5 rounded-lg mb-1 transition-all group ${
                          isComplete ? 'bg-green-50/50' : 
                          isGated ? 'bg-gray-50 opacity-50 cursor-not-allowed' :
                          'hover:bg-gray-50 cursor-pointer'
                        }`}
                        data-testid={`setup-step-${step.id}`}
                      >
                        <div className="flex items-center gap-3">
                          {/* Icon */}
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            isComplete ? 'bg-green-500 text-white' : 
                            isGated ? 'bg-gray-200 text-gray-400' :
                            step.color + ' text-white'
                          }`}>
                            {isComplete ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`font-medium text-sm ${
                                isComplete ? 'text-green-700' : 
                                isGated ? 'text-gray-400' :
                                'text-gray-900'
                              }`}>
                                {step.title}
                              </span>
                              {step.isExternal && <ExternalLink className="w-3 h-3 text-gray-400" />}
                            </div>
                            <p className={`text-xs ${isGated ? 'text-gray-400' : 'text-gray-500'}`}>
                              {isGated ? step.gateMessage : step.description}
                            </p>
                            {step.hint && !isGated && (
                              <p className="text-xs text-blue-600 mt-0.5 font-medium">{step.hint}</p>
                            )}
                          </div>
                          
                          {/* Arrow */}
                          {!isGated && (
                            <ChevronDown className="w-4 h-4 -rotate-90 text-gray-300 group-hover:text-blue-600 flex-shrink-0" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      {completedCoreSteps === totalCoreSteps && !completionData.stripeIsLive && (
        <div className="p-3 bg-emerald-50 border-t border-emerald-100">
          <div className="flex items-center gap-2 text-emerald-700">
            <Rocket className="w-4 h-4" />
            <span className="text-sm font-medium">Ready to go live! Switch Stripe to live mode.</span>
          </div>
        </div>
      )}
      
      {completionData.stripeIsLive && (
        <div className="p-3 bg-gradient-to-r from-emerald-50 to-teal-50 border-t border-emerald-100">
          <div className="flex items-center gap-2 text-emerald-700">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-medium">🎉 You're live! Accepting real payments.</span>
          </div>
        </div>
      )}

      {/* Help Link */}
      <div className="p-3 bg-gray-50 border-t border-gray-200">
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('openHelpGuide', { detail: { section: 'welcome' } }))}
          className="w-full text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center justify-center gap-2"
        >
          <HelpCircle className="w-4 h-4" />
          <span>View Help Guide</span>
        </button>
      </div>
    </div>
  );
};

export default SetupGuideWidget;
